import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env';
import { map } from 'rxjs/operators';
import {
  SmsSender,
  SmsSenderAgcom,
  SmsSenders,
  SmsSendersAgcom,
  SmsSendersAgcomSisNumeric,
  SmsSendersAgcomSisAlphaNumeric,
  SmsSenderAgcomSisAlphaNumeric
} from '@models/sms/sender';
import { flattenObject } from '@utils/utils';

@Injectable({
  providedIn: 'root'
})
export class SenderService {
  private readonly baseUrl;
  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getAgcomSenders(all: boolean = true, page: number = null, size: number = null, type: string = null): Observable<SmsSendersAgcom> {
    let queryParams = new HttpParams().set('all', String(all));
    if (page) {
      queryParams = queryParams.set('page', String(page));
    }
    if (size) {
      queryParams = queryParams.set('size', String(size));
    }
    if (type) {
      queryParams = queryParams.set('numeric', type);
    }
    return this.http
      .get<Response>(this.baseUrl + 'sms/valid-sender', { params: queryParams })
      .pipe(map(data => SmsSendersAgcom.deserialize(data)));
  }

  getAgcomSisSendersNumeric(all: boolean = true, page: number = null, size: number = null): Observable<SmsSendersAgcomSisNumeric> {
    let queryParams = new HttpParams().set('all', String(all));
    if (page) {
      queryParams = queryParams.set('page', String(page));
    }
    if (size) {
      queryParams = queryParams.set('size', String(size));
    }
    return this.http
      .get<Response>(this.baseUrl + 'sms/valid-sender/numeric', { params: queryParams })
      .pipe(map(data => SmsSendersAgcomSisNumeric.deserialize(data)));
  }

  getAgcomSisSendersAlphaNumeric(
    all: boolean = true,
    page: number = null,
    size: number = null
  ): Observable<SmsSendersAgcomSisAlphaNumeric> {
    let queryParams = new HttpParams().set('all', String(all));
    if (page) {
      queryParams = queryParams.set('page', String(page));
    }
    if (size) {
      queryParams = queryParams.set('size', String(size));
    }
    return this.http
      .get<Response>(this.baseUrl + 'sms/valid-sender/alphanumeric', { params: queryParams })
      .pipe(map(data => SmsSendersAgcomSisAlphaNumeric.deserialize(data)));
  }

  getUniqueAgcomSisSendersAlphaNumeric(): Observable<SmsSenderAgcomSisAlphaNumeric[]> {
    return this.http.get<Response[]>(this.baseUrl + 'sms/valid-sender/unique-senders', {}).pipe(
      map(data => {
        return data.map(d => SmsSenderAgcomSisAlphaNumeric.deserialize(d));
      })
    );
  }

  getAllSendersNumericAndAlphaNumeric(all: boolean = true, page: number = null, size: number = null): Observable<SmsSender[]> {
    return new Observable(observer => {
      this.getAgcomSisSendersNumeric(all, page, size)
        .pipe(
          map(data => {
            const newData = [];
            for (const iterator of data.data) {
              const sender = {
                sender: iterator.sender,
                id: iterator.idSender
              };
              newData.push(sender);
            }
            return newData;
          })
        )
        .subscribe(sendersNumeric => {
          // on reajuste le size en fonction des resultats précédents
          if (size) {
            size = size - sendersNumeric.length;
          }
          this.getAgcomSisSendersAlphaNumeric(all, page, size)
            .pipe(
              map(data => {
                const newData = [];
                for (const iterator of data.data) {
                  const sender = {
                    sender: iterator.sender,
                    id: iterator.idSender,
                    type: iterator.type
                  };
                  newData.push(sender);
                }
                return newData;
              })
            )
            .subscribe(sendersAlpha => {
              observer.next([...sendersNumeric, ...sendersAlpha]);
              observer.complete();
            });
        });
    });
  }

  removeAgcomSender(smsSenderAgcom: SmsSenderAgcom) {
    return this.http.delete(this.baseUrl + 'sms/valid-sender/' + smsSenderAgcom.idSender, { responseType: 'text' as 'json' });
  }

  getAgcomSender(idSender): Observable<SmsSenderAgcom> {
    return this.http.get<Response>(this.baseUrl + 'sms/valid-sender/' + idSender, {}).pipe(map(data => SmsSenders.deserialize(data)));
  }

  getNormalSenders(): Observable<SmsSenders> {
    return this.http.get<Response>(this.baseUrl + 'sms/sender', {}).pipe(
      map(data => {
        return SmsSenders.deserialize({
          data: (data ? ((data as any) as Array<string>) : []).map(data2 => SmsSender.deserialize(data2)),
          total: (data as any).length
        });
      })
    );
  }

  addNormalSender(sender: SmsSender): Observable<any> {
    return this.http.post(this.baseUrl + 'sms/sender', sender, {});
  }

  deleteNormalSender(sender: SmsSender) {
    return this.http.delete(this.baseUrl + 'sms/sender/' + sender.id, { responseType: 'text' as 'json' });
  }

  addAgcomSenderAlpha(sender: SmsSenderAgcom, sisService: boolean = false): Observable<any> {
    const request = flattenObject(sender);
    (request as any).addToSenderIdService = sisService;
    (request as any).companyInfo.legalAgreement = true;
    return this.http.post(this.baseUrl + 'sms/valid-sender', request, {});
  }

  editAgcomSenderAlpha(idSender, pec): Observable<any> {
    const patch = [
      {
        op: 'replace',
        path: '/tpoaUser/contactInfo',
        value: pec
      },
      {
        op: 'replace',
        path: '/tpoaUser/legalAgreement',
        value: true
      }
    ];
    const patchHeaders = new HttpHeaders().set('Content-type', 'application/json-patch+json');
    return this.http.patch<Response>(this.baseUrl + 'sms/valid-sender/' + idSender, patch, { headers: patchHeaders });
  }

  uploadFileSmsSenderAlpha(files: File[], sisRequestId) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file, file.name);
    });
    formData.append('request-id', sisRequestId);

    return this.http.post(this.baseUrl + 'sms/valid-sender/upload-document', formData);
  }

  addAgcomSenderNumeric(sender: SmsSenderAgcom): Observable<any> {
    const request = {
      sender: sender.sender,
      addToSenderIdService: false,
      companyInfo: {
        legalAgreement: true
      }
    };
    return this.http.post(this.baseUrl + 'sms/valid-sender', request, {});
  }

  getDestinationTerritoriesValidSenders() {
    return this.http.get<Response>(this.baseUrl + 'sms/valid-sender/destinationTerritories ');
  }
}
